import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, LOGOS } from "@/brand";
import { useCart } from "@/context/CartContext";
import Crown from "@/components/Crown";

const FREE_SHIPPING = 200;

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, updateQty, removeItem, subtotal } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);

  const checkout = async () => {
    if (!items.length) return;
    setCheckingOut(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        items: items.map((i) => ({ lookup_key: i.lookup_key, quantity: i.quantity, size: i.size })),
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "CHECKOUT FAILED — TRY AGAIN");
      setCheckingOut(false);
    }
  };

  const progress = Math.min(100, (subtotal / FREE_SHIPPING) * 100);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 z-[60] bg-[#0A0A0B]/70 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="fixed right-0 top-0 bottom-0 z-[61] flex w-full max-w-md flex-col border-l border-zinc-800 bg-[#0E0E10]"
          >
            <div className="flex items-center justify-between border-b border-zinc-800 px-6 py-5">
              <div className="flex items-center gap-3">
                <img src={LOGOS.monogram} alt="" className="logo-blend h-9 w-auto" />
                <span className="font-display text-xl tracking-[0.2em] chrome-text-bright uppercase">Your Vault</span>
              </div>
              <button data-testid="cart-drawer-close" onClick={() => setIsOpen(false)} className="text-zinc-500 hover:text-zinc-100 transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-4 border-b border-zinc-800/70">
              <p className="mb-2 text-[9px] tracking-[0.3em] text-zinc-500 uppercase">
                {subtotal >= FREE_SHIPPING
                  ? "Free worldwide shipping unlocked"
                  : `$${(FREE_SHIPPING - subtotal).toFixed(0)} away from free worldwide shipping`}
              </p>
              <div className="h-px w-full bg-zinc-800">
                <motion.div className="h-px bg-zinc-200" animate={{ width: `${progress}%` }} transition={{ duration: 0.6 }} />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-4" data-testid="cart-empty-state">
                  <Crown className="h-8 w-12 text-zinc-700" />
                  <p className="text-[10px] tracking-[0.35em] text-zinc-600 uppercase">The vault is empty</p>
                </div>
              ) : (
                <ul className="flex flex-col divide-y divide-zinc-800/70">
                  {items.map((i) => (
                    <li key={i.key} data-testid={`cart-item-${i.lookup_key}-${i.size}`} className="flex gap-4 py-4">
                      <div className="h-20 w-16 shrink-0 overflow-hidden border border-zinc-800">
                        <img src={i.image} alt={i.name} className="h-full w-full object-cover grayscale-[30%]" />
                      </div>
                      <div className="flex flex-1 flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-xs font-bold tracking-wider text-zinc-100 uppercase">{i.name}</p>
                            <p className="mt-0.5 text-[9px] tracking-[0.3em] text-zinc-500 uppercase">Size {i.size}</p>
                          </div>
                          <button
                            data-testid={`cart-remove-${i.lookup_key}-${i.size}`}
                            onClick={() => removeItem(i.key)}
                            className="text-zinc-600 hover:text-red-800 transition-colors"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <div className="mt-auto flex items-center justify-between pt-2">
                          <div className="flex items-center border border-zinc-800">
                            <button
                              data-testid={`cart-qty-minus-${i.lookup_key}-${i.size}`}
                              onClick={() => updateQty(i.key, -1)}
                              className="px-2.5 py-1.5 text-zinc-400 hover:text-zinc-100"
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="px-2 text-xs text-zinc-200">{i.quantity}</span>
                            <button
                              data-testid={`cart-qty-plus-${i.lookup_key}-${i.size}`}
                              onClick={() => updateQty(i.key, 1)}
                              className="px-2.5 py-1.5 text-zinc-400 hover:text-zinc-100"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                          <span className="text-sm text-zinc-200">${(i.price * i.quantity).toFixed(0)}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="border-t border-zinc-800 px-6 py-6">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-[10px] tracking-[0.35em] text-zinc-500 uppercase">Subtotal</span>
                <span data-testid="cart-subtotal" className="font-display text-2xl chrome-text-bright">${subtotal.toFixed(2)}</span>
              </div>
              <button
                data-testid="cart-checkout-button"
                onClick={checkout}
                disabled={!items.length || checkingOut}
                className="w-full border border-zinc-400 bg-zinc-100 py-4 text-xs font-bold tracking-[0.35em] text-zinc-950 uppercase transition-colors duration-300 hover:bg-transparent hover:text-zinc-100 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {checkingOut ? "Opening Secure Checkout…" : "Checkout — Stripe Secure"}
              </button>
              <p className="mt-3 text-center text-[9px] tracking-[0.25em] text-zinc-600 uppercase">
                Taxes calculated at checkout
              </p>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
